import {TheRaveParsedData} from "./TheRaveParsedData";
import {
    IBetWinData,
    ITheRaveGameData,
    ISpecialSymbolData,
    IEnhancerReel,
    ITheRaveInitData,
    IBetWayWinData
} from "./ITheRaveData";
import {TheRaveDataParser} from "./TheRaveDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {TheRaveGameConfig} from "../../TheRaveGameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TheRaveGameMode} from "../../gameflow/TheRaveGameMode";
import {TheRave} from "../../TheRave";

export class TheRaveParsedGameData extends TheRaveParsedData {

    public readonly betWins: IBetWinData[];
    public readonly splitDataListxWays: ISpecialSymbolData[];
    public readonly singleWin: number;
    public readonly playedBetValue: number;
    public readonly featureBet: boolean;
    public readonly nearWinReels: boolean[];
    public readonly nearWinBigWinReels: boolean[];
    public readonly XSPositions: number[];
    public readonly hasXSplit: boolean;
    public readonly hasXWays: boolean;
    public readonly xWaysSizeEachReel: number[];
    public readonly brokeTheBank: boolean;
    public readonly currentWinRatioConfig: IWinRatioConfig | null; // Calculated values
    public readonly winsPositions: boolean[][];
    public readonly symbolSizes: number[][];
    public readonly totalWays: number;
    public readonly evaluatedArea: string[][];
    public readonly isFreespin: boolean;
    public readonly activeReel: boolean[];
    public readonly doDoubleXways: boolean[];
    public readonly doLinkedReels: boolean[];
    public readonly doLowToWildReels: boolean[];
    public readonly doTransformToM1: boolean;
    public readonly enhancersNum: number;
    public readonly scattersNum: number;
    public readonly scattersNumInFs: number;
    public readonly mysterySymbolReveal: string;
    public readonly lastLinkedReelId: number;
    public readonly firstLinkedReelId: number;
    public readonly potentialWin: boolean;
    public readonly wasFeatureBuy:boolean;
    public readonly enhancedBet:boolean;
    public readonly potentialWinPos:boolean[][];

    constructor(rawData: ITheRaveGameData, parser: TheRaveDataParser) {
        super(rawData, parser);
        this.evaluatedArea = rawData.evaluatedArea;

        this.betWins = parser.parseBetWins(rawData);
        this.potentialWin = parser.parsePotentialWin(rawData);
        this.potentialWinPos = parser.parsePotentialWinPos(rawData);
        this.winsPositions = parser.parseBetWinPositions(this.betWins);
        this.singleWin = rawData.totalSpinWinnings;
        this.playedBetValue = parseFloat(rawData.playedBetValue);
        this.currentWinRatioConfig = parser.parseWinRatio(this.singleWin / this.playedBetValue);

        this.nearWinBigWinReels = this.getRandomBigWinNearWin();
        this.nearWinReels = parser.parseNearWinReels(this.scatterPositions);
        this.featureBet = rawData.featureBet;
        this.symbolSizes = rawData.symbolSizes;
        this.doLowToWildReels = rawData.doLowToWildReels;
        this.doDoubleXways = rawData.doDoubleXways;
        this.doLinkedReels = rawData.doLinkedReels;
        this.doTransformToM1 = rawData.doTransformToM1;
        this.scattersNum = (rawData.freespinTriggeredThisSpin ? this.getScattersNum(rawData) : 0);
        this.enhancersNum = Math.max(rawData.scatterEnhancers.length, this.scattersNum);
        this.wasFeatureBuy = rawData.wasFeatureBuy;

        //xways
        this.hasXWays = rawData.splitDataListxWays.length > 0;
        this.xWaysSizeEachReel = rawData.xWaysSizeEachReel;
        this.splitDataListxWays = rawData.splitDataListxWays;
        this.mysterySymbolReveal = this.hasXWays?rawData.splitDataListxWays[0].symName:"";

        //split
        this.XSPositions = this.parseXS(this.reels);
        this.hasXSplit = this.XSPositions.length > 0;

        //fs
        this.isFreespin = rawData.mode != GameMode.NORMAL;
        this.scattersNumInFs = (this.isFreespin? this.getScattersNum(rawData) : 0);
        const linkedReelRange:number[] = this.parseLastLinkedReelId(rawData);
        this.firstLinkedReelId = linkedReelRange[0];
        this.lastLinkedReelId = linkedReelRange[1];

        this.activeReel = this.parseActiveReel(this.reels);
        this.totalWays = rawData.totalWays;
        this.brokeTheBank = rawData.maximumWinAchieved;
        this.enhancedBet = this.getBoostedBet(rawData);
    }

    private parseLastLinkedReelId(rawData: ITheRaveGameData): number[] {
        if(rawData.mode[0] === "N"){
            return [-1,-1];
        }
        let hasLinkedReel:boolean = false,first:number = -1,last:number = -1;
        rawData.doLinkedReels.forEach((doLinkedReel:boolean, i:number)=>{
            if(doLinkedReel){
                if(!hasLinkedReel){
                    first = i;
                    hasLinkedReel = true;
                }
                last = i;
            }
        });
        return [first,last];
    }
    private getScattersNum(rawData: ITheRaveGameData): number {
        let scattersNum: number = 0;
        rawData.scatterPositions.forEach((pos: number) => {
            scattersNum += (pos > -1 ? 1 : 0);
        });
        return scattersNum;
    }

    private getRandomBigWinNearWin(): boolean[] {
        const values = ArrayHelper.initArrayWithValues(TheRaveGameConfig.REELS_NUM, () => false);
        values[4] = this.getRandomBigWinTease();

        return values;
    }

    private parseXS(reels: string[][]): number[] {
        const values = ArrayHelper.initArrayWithValues(TheRaveGameConfig.REELS_NUM, () => -1);
       reels.forEach((symbol: string[], i: number) => {
            if ((i>0&&i<4) && symbol[i] === "XS") {
                values[i] = i-1;
            }
        });
        return values;
    }

    private parseActiveReel(reels: string[][]): boolean[] {
        let activeReels: boolean[] = ArrayHelper.initArrayWithValues(TheRaveGameConfig.SPIN_REELS, () => true);
        reels.forEach((symbol: string[], i: number) => {
            if (i > 4 && symbol[0] === "X") {
                activeReels[i] = false;
            }
        });
        return activeReels;
    }

    private getRandomBigWinTease(): boolean {
        if (this.totalWin === 0) {
            return false;
        }
        switch (this.mode) {
            case GameMode.NORMAL:
                return this.currentWinRatioConfig!.ratio + Math.random() * 15 > 35;
            case GameMode.FREESPIN:
                return this.currentWinRatioConfig!.ratio + Math.random() * 20 > 50;
            case TheRaveGameMode.FREESPIN_SUPER:
                return this.currentWinRatioConfig!.ratio + Math.random() * 300 > 600;
            default:
                return false;
        }
    }
    private getBoostedBet(rawData:ITheRaveGameData):boolean {
        if(rawData.enhancedBet) {
            if(!TheRave.reels[1] || !TheRave.reels[1].isBoostActive()) {
                debugger;
                throw new Error(`Server enhancedBet doesn't match the display.`);
            }
        }
        return rawData.enhancedBet;
    }

}